import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Search, ArrowLeft, Send, Check } from "lucide-react";
import { API } from "../api";
import { ActionButton, Confirmation, ErrorNote, Spinner } from "./Feedback";

/** Search the organisations that exist, and ask one of them to admit you. */
export default function OrgPicker({ onDone, onCancel }) {
  const [query, setQuery] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [searching, setSearching] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState("");
  const latest = useRef(0);

  // Debounced so typing does not fire a Keycloak lookup per keystroke. A request
  // counter keeps a slow earlier response from overwriting a newer one.
  useEffect(() => {
    setSearching(true);
    const ticket = ++latest.current;
    const timer = setTimeout(async () => {
      try {
        const res = await API.get("/api/orgs", { params: { q: query } });
        if (ticket === latest.current) setOrgs(res.data.organizations || []);
      } catch {
        if (ticket === latest.current) setOrgs([]);
      } finally {
        if (ticket === latest.current) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      await API.post("/api/orgs/join-requests", { org_id: selected.id });
      setSent(selected.name);
      setTimeout(() => onDone(), 1200);
    } catch (err) {
      setError(err.response?.data?.detail || "We could not send that request.");
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <Confirmation
          title={`Request sent to ${sent}`}
          detail="Its administrators have been notified by email. You will be let in as soon as one of them adds you."
        />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
        <h2 className="text-sm font-black tracking-tight text-slate-900">
          Find your organisation
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-bold text-slate-500 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
        >
          <ArrowLeft size={13} /> Back
        </button>
      </div>

      <div className="p-6">
        <div className="relative">
          <Search
            size={15}
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Start typing a name…"
            aria-label="Search organisations"
            className="w-full rounded-xl border border-slate-300 py-3 pl-11 pr-11 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
          />
          {searching && (
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
              <Spinner size={13} />
            </span>
          )}
        </div>

        <div
          className="mt-4 max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200"
          role="listbox"
          aria-label="Organisations"
        >
          {searching && orgs.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">Searching…</p>
          ) : orgs.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm leading-relaxed text-slate-500">
              No organisations match that.
              <br />
              If yours is not listed, go back and propose it.
            </p>
          ) : (
            orgs.map((org) => {
              const isSelected = selected?.id === org.id;
              return (
                <button
                  key={org.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => setSelected(org)}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green-600 ${
                    isSelected ? "bg-green-50" : "hover:bg-slate-50"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold text-slate-900">
                      {org.name}
                    </span>
                    {org.domains?.length > 0 && (
                      <span className="mt-0.5 block truncate text-xs text-slate-500">
                        {org.domains.join(" · ")}
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <motion.span
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-600 text-white"
                    >
                      <Check size={12} strokeWidth={4} />
                    </motion.span>
                  )}
                </button>
              );
            })
          )}
        </div>

        {error && (
          <div className="mt-4">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <ActionButton
            icon={Send}
            busy={busy}
            busyLabel="Sending request…"
            disabled={!selected}
            onClick={submit}
          >
            {selected ? `Request to join ${selected.name}` : "Select an organisation"}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}
